import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Search, 
  ThumbsUp, 
  ThumbsDown, 
  Filter, 
  ChevronDown,
  CheckCircle,
  Loader2,
  MessageCircle,
  Mail,
  HelpCircle,
  Sparkles,
  ArrowRight
} from "lucide-react";
import { useFAQ } from "@/hooks/useFAQ";
import type { FAQ } from "@/services/faqService";
import { getFAQSEO, validateSEOData, generateKeywords } from "@/services/seoService";

const FAQ = () => {
  const [totalNavHeight, setTotalNavHeight] = useState(112);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showCategories, setShowCategories] = useState(false);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  
  const { 
    faqs, 
    groupedFAQs, 
    categories, 
    loading, 
    error, 
    trackView, 
    markHelpful, 
    votedFAQs 
  } = useFAQ();

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  // Filter FAQs based on search and category, then group them
  const filteredGroupedFAQs = React.useMemo(() => {
    let filtered = faqs;

    // Filter by category
    if (selectedCategory !== "All") {
      filtered = groupedFAQs[selectedCategory] || [];
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Group the filtered results by category
    const grouped = filtered.reduce((acc, faq) => {
      const category = faq.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(faq);
      return acc;
    }, {} as Record<string, FAQ[]>);

    // Sort FAQs within each category by order
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => a.order - b.order);
    });

    return grouped;
  }, [faqs, groupedFAQs, selectedCategory, searchQuery]);

  // Handle FAQ item click (track view only on open)
  const handleFAQValueChange = async (value: string) => {
    if (value && !openItems.has(value)) {
      // Item is being opened
      setOpenItems(prev => new Set(prev).add(value));
      await trackView(value);
    } else if (!value || !openItems.has(value)) {
      // Item is being closed - don't track anything
      if (value) {
        setOpenItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(value);
          return newSet;
        });
      }
    }
  };

  // Handle helpful rating
  const handleHelpfulRating = async (faqId: string, isHelpful: boolean) => {
    await markHelpful(faqId, isHelpful);
  };

  // Get category icon - matches admin portal categories exactly
  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      // Primary admin portal categories (exact match)
      'General': '💬',
      'Orders & Shipping': '📦',
      'Returns & Refunds': '↩️',
      'Products': '🛍️',
      'Account': '👤',
      'Payment': '💳',
      'Technical Support': '🔧',
      'Policies': '📋',
      
      // Backward compatibility for legacy categories
      'Shipping': '📦',  // Maps to Orders & Shipping
      'Orders': '📦',    // Maps to Orders & Shipping
      'Returns': '↩️',   // Maps to Returns & Refunds
      'Support': '🔧'    // Maps to Technical Support
    };
    return icons[category] || '📋';
  };

  if (loading) {
    return (
      <div 
        className="flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-3xl flex items-center justify-center shadow-2xl mx-auto mb-6">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
          <p className="text-lg font-semibold text-jamaica-green">Loading FAQs...</p>
        </motion.div>
      </div>
    );
  }

  // Generate SEO data for FAQ page
  const seoData = validateSEOData(getFAQSEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {seoData.keywords && (
          <meta name="keywords" content={generateKeywords(seoData.keywords)} />
        )}
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoData.url} />
        
        {/* Structured Data - JSON-LD Schema */}
        {seoData.schema && seoData.schema.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
      
    <div 
      className="flex flex-col"
      style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
    >
      <main className="flex-grow relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
          
          {/* Triangle Decorative Elements */}
          <div className="absolute top-1/4 right-10 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[35px] border-l-transparent border-r-transparent border-b-jamaica-green/10 animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/3 left-16 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[26px] border-l-transparent border-r-transparent border-b-jamaica-yellow/15 animate-bounce delay-1200"></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(0, 116, 47, 0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 116, 47, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Enhanced Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6">
                  <HelpCircle className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                  <MessageCircle className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-4 py-1.5 rounded-2xl border border-jamaica-green/20 shadow-lg">
                <span className="text-sm font-bold text-jamaica-black flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-jamaica-green" />
                  Help Center
                </span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
              <span className="bg-gradient-to-r from-gray-900 via-jamaica-green to-gray-900 bg-clip-text text-transparent p-1">
                Frequently Asked Questions
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
              Find answers to common questions about our 
              <span className="text-jamaica-yellow font-semibold"> Jamaican care packages</span>, 
              <span className="text-jamaica-green font-semibold"> shipping</span>, and more
            </p>

            {/* Enhanced Search Bar */}
            <div className="max-w-2xl mx-auto mb-4">
              <div className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 p-2 hover:shadow-2xl transition-all duration-500">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search frequently asked questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 text-lg bg-transparent border-0 focus:outline-none focus:ring-4 focus:ring-jamaica-green/10 rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Enhanced Category Filter */}
            <div>
              <div className="relative inline-block">
                <Button
                  variant="outline"
                  onClick={() => setShowCategories(!showCategories)}
                  className="flex items-center gap-2 bg-white/80 backdrop-blur-sm border-2 hover:bg-white/90 shadow-lg rounded-xl px-5 py-2.5"
                >
                  <Filter className="h-4 w-4" />
                  {selectedCategory === "All" ? "All Categories" : selectedCategory}
                  <ChevronDown className={`h-4 w-4 transition-transform ${showCategories ? 'rotate-180' : ''}`} />
                </Button>
                
                <AnimatePresence>
                  {showCategories && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute top-full mt-2 left-0 right-0 bg-white/90 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[200px]"
                    >
                      <button
                        onClick={() => {
                          setSelectedCategory("All");
                          setShowCategories(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                          selectedCategory === "All" ? "bg-jamaica-green/10 text-jamaica-green font-medium" : ""
                        }`}
                      >
                        All Categories
                      </button>
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => {
                            setSelectedCategory(category);
                            setShowCategories(false);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                            selectedCategory === category ? "bg-jamaica-green/10 text-jamaica-green font-medium" : ""
                          }`}
                        >
                          <span>{getCategoryIcon(category)}</span>
                          {category}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
            </div>
          </div>
          </motion.div>
        </div>
        
        {/* FAQ Content */}
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-xl text-red-800"
            >
              {error}
            </motion.div>
          )}

          {Object.keys(filteredGroupedFAQs).length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 p-12">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">No FAQs found</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {searchQuery ? "Try adjusting your search terms or category filter." : "No FAQs are available at the moment."}
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              {Object.entries(filteredGroupedFAQs).map(([category, categoryFAQs], categoryIndex) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: categoryIndex * 0.1 }}
                  className="space-y-4"
                >
                  {/* Category Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-jamaica-yellow to-jamaica-yellow/80 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-xl">{getCategoryIcon(category)}</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{category}</h2>
                      <p className="text-sm text-gray-600">{categoryFAQs.length} questions</p>
                    </div>
                  </div>

                  {/* FAQs for this category */}
                  <Accordion type="single" collapsible className="space-y-3" onValueChange={handleFAQValueChange}>
                    {categoryFAQs.map((faq, index) => (
                      <motion.div
                        key={faq.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: (categoryIndex * 0.1) + (index * 0.05) }}
                      >
                        <AccordionItem 
                          value={faq.id} 
                          className="border-0 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
                        >
                          <AccordionTrigger className="px-6 py-6 text-left font-medium text-gray-900 hover:no-underline hover:bg-gray-50/50 transition-colors rounded-t-2xl [&[data-state=open]]:rounded-b-none">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {faq.question}
                            </h3>
                </AccordionTrigger>
                          <AccordionContent className="px-6 pb-6">
                            <div className="pt-4 border-t border-gray-100">
                              <div className="prose prose-gray max-w-none">
                                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                  {faq.answer}
                                </p>
                              </div>
                              
                              {/* Subtle Helpful Rating */}
                              <div className="mt-6 pt-4 border-t border-gray-100">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm text-gray-500">
                                    Was this helpful?
                                  </p>
                                  <div className="flex items-center gap-2">
                                    {votedFAQs.has(faq.id) ? (
                                      <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <span>Thanks for your feedback!</span>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => handleHelpfulRating(faq.id, true)}
                                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                                          title="Yes, this was helpful"
                                        >
                                          <ThumbsUp className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={() => handleHelpfulRating(faq.id, false)}
                                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                          title="No, this wasn't helpful"
                                        >
                                          <ThumbsDown className="h-4 w-4" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                </AccordionContent>
              </AccordionItem>
                      </motion.div>
            ))}
          </Accordion>
                </motion.div>
              ))}
            </motion.div>
          )}
          
          {/* Enhanced Contact Support Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-16"
          >
            <Card className="relative bg-gradient-to-br from-jamaica-green/5 to-jamaica-yellow/5 border-0 shadow-xl backdrop-blur-sm overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-jamaica-yellow/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-jamaica-green/10 rounded-full translate-y-12 -translate-x-12"></div>
              
              <CardContent className="relative p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl mb-6 shadow-lg">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Still have questions?</h2>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Can't find what you're looking for? Our friendly customer service team is here to help you with any questions about our Jamaican care packages.
                </p>
                <div className="flex flex-col gap-4 items-center justify-center">
                  <Button asChild className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg hover:shadow-xl transition-all duration-300 px-6 py-3 rounded-xl">
                    <a href="mailto:support@jampackd.store" className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email Support
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    We typically respond within 24 hours
                  </p>
            </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
    </>
  );
};

export default FAQ;
